import type { AvailabilityKind } from '../agent/availabilityStore'

/**
 * Availability read against the schedule (docs/product/scheduling-model.md's
 * "Availability"). Pure: the planning board loads the rules and the approved
 * exceptions and asks, for an employee and a date, what they said and whether
 * a work entry runs into time they said they cannot work. Advisory only; the
 * board flags a conflict and never blocks one.
 */
export type AvailabilityRuleRecord = {
  employeeId: string
  weekday: number
  // Both absent for a rule that covers the whole day.
  startTime?: string
  endTime?: string
  kind: AvailabilityKind
  effectiveFrom: string
  effectiveTo?: string
}

// Only approved exceptions are given here; a pending, returned or denied one
// says nothing about the schedule.
export type AvailabilityExceptionRecord = {
  employeeId: string
  startDate: string
  endDate: string
  startTime?: string
  endTime?: string
  kind: AvailabilityKind
}

export type AvailabilityWindow = {
  // Minutes from midnight, end exclusive; a whole day is 0 to 1440.
  start: number
  end: number
  kind: AvailabilityKind
}

export type DayAvailability = {
  // Where the day's windows came from: an approved exception, the recurring
  // week, or nothing set (an open day).
  source: 'exception' | 'rule' | 'none'
  windows: AvailabilityWindow[]
}

const DAY = 1440

export function minutesOf(time: string): number {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

// A window on one date. One that ends after midnight runs to the end of its own
// date, with the hours it covers on the following date in wrapEnd.
type Piece = AvailabilityWindow & { wrapEnd?: number }

function windowOf(record: { startTime?: string; endTime?: string; kind: AvailabilityKind }): Piece {
  if (!record.startTime || !record.endTime) return { start: 0, end: DAY, kind: record.kind }
  const start = minutesOf(record.startTime)
  const end = minutesOf(record.endTime)
  return end > start ? { start, end, kind: record.kind } : { start, end: DAY, kind: record.kind, wrapEnd: end }
}

// UTC, since only the calendar date matters and no timezone may move it.
function weekdayOf(date: string): number {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}

function shiftDate(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
}

// What the date's own source says: an approved exception covering the date
// replaces the recurring week for it, otherwise the rules for its weekday in
// effect on it apply.
function ownPieces(
  employeeId: string,
  date: string,
  rules: AvailabilityRuleRecord[],
  exceptions: AvailabilityExceptionRecord[],
): { source: DayAvailability['source']; pieces: Piece[] } {
  const covering = exceptions.filter((item) => item.employeeId === employeeId && item.startDate <= date && date <= item.endDate)
  if (covering.length > 0) return { source: 'exception', pieces: covering.map(windowOf) }

  const weekday = weekdayOf(date)
  const applying = rules.filter(
    (rule) => rule.employeeId === employeeId && rule.weekday === weekday && rule.effectiveFrom <= date && (!rule.effectiveTo || date <= rule.effectiveTo),
  )
  return applying.length > 0 ? { source: 'rule', pieces: applying.map(windowOf) } : { source: 'none', pieces: [] }
}

/**
 * What one employee said about one date: the date's own windows, plus the
 * early hours of any window from the day before that ran past midnight.
 *
 * An approved exception covering the date replaces the recurring week for it,
 * and that includes the early hours a recurring overnight window from the day
 * before would carry in: the agent has said how the date stands. The early hours
 * of an exception from the day before still carry in, since that is the agent's
 * own explicit word for those hours.
 */
export function availabilityFor(
  employeeId: string,
  date: string,
  rules: AvailabilityRuleRecord[],
  exceptions: AvailabilityExceptionRecord[],
): DayAvailability {
  const own = ownPieces(employeeId, date, rules, exceptions)
  const before = ownPieces(employeeId, shiftDate(date, -1), rules, exceptions)
  const carried: AvailabilityWindow[] =
    own.source === 'exception' && before.source !== 'exception'
      ? []
      : before.pieces.flatMap((piece) => (piece.wrapEnd && piece.wrapEnd > 0 ? [{ start: 0, end: piece.wrapEnd, kind: piece.kind }] : []))

  const windows = [...own.pieces.map(({ start, end, kind }) => ({ start, end, kind })), ...carried]
  const source = own.source !== 'none' ? own.source : carried.length > 0 ? before.source : 'none'
  return { source, windows }
}

export type WorkWindow = {
  start: number
  end: number
  // For a shift that runs past midnight: the minutes of the following day it
  // covers, from 00:00 to this time.
  overnightEnd?: number
}

/**
 * The hours of a work entry, from its label ("0700–1500"). A label with no
 * hours, such as an assignment worked across the whole day, reads as the
 * whole day. A shift that ends after midnight runs to the end of its own
 * date, with the rest on the following date in overnightEnd.
 */
export function workWindowOf(label: string): WorkWindow {
  const match = /(\d{2})(\d{2})–(\d{2})(\d{2})/.exec(label)
  if (!match) return { start: 0, end: DAY }
  const start = Number(match[1]) * 60 + Number(match[2])
  const end = Number(match[3]) * 60 + Number(match[4])
  return end > start ? { start, end } : { start, end: DAY, ...(end > 0 ? { overnightEnd: end } : {}) }
}

// The calendar day after an ISO date, in UTC so no timezone moves it.
export function nextDate(date: string): string {
  return shiftDate(date, 1)
}

export type AvailabilityConflict = {
  kind: 'unavailable'
  source: 'exception' | 'rule'
  // True when the conflict is in the hours after midnight, on the day after
  // the one the shift starts on.
  overnight?: boolean
}

/**
 * Whether a work entry overlaps time the employee said they cannot work, or
 * null. Time marked available or preferred, or nothing set, is no conflict.
 */
export function conflictFor(day: DayAvailability, work: { start: number; end: number }): AvailabilityConflict | null {
  const overlaps = day.windows.some((window) => window.kind === 'unavailable' && window.start < work.end && work.start < window.end)
  return overlaps && day.source !== 'none' ? { kind: 'unavailable', source: day.source } : null
}

/**
 * A work entry that starts on `date`, read against that date and, for a shift
 * that runs past midnight, against the following date for the hours it
 * covers there.
 */
export function workConflictFor(
  employeeId: string,
  date: string,
  work: WorkWindow,
  rules: AvailabilityRuleRecord[],
  exceptions: AvailabilityExceptionRecord[],
): AvailabilityConflict | null {
  const sameDay = conflictFor(availabilityFor(employeeId, date, rules, exceptions), work)
  if (sameDay || work.overnightEnd === undefined) return sameDay

  const after = conflictFor(availabilityFor(employeeId, nextDate(date), rules, exceptions), { start: 0, end: work.overnightEnd })
  return after ? { ...after, overnight: true } : null
}
